import { type ComponentProps, type ReactNode, useCallback, useState } from 'react';
import {
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxMultipleLabel,
  type ComboboxPresetBaseProps,
  ComboboxRoot,
  type ComboboxRootProps,
  ComboboxValue,
  useComboboxAnchor,
} from './combobox';
import {
  ComboboxPresetContent,
  type CreatableMarker,
  type CreatableProps,
  isCreatable,
  type QueryProps,
  useComboboxInputValue,
  useCreatableHelpers,
  useCreatableItemEquality,
} from './combobox-internals';

export type MultiComboboxProps<T> = ComboboxPresetBaseProps<T> &
  Pick<ComboboxRootProps<T, true>, 'isItemEqualToValue' | 'itemToStringLabel' | 'disabled'> &
  Pick<ComponentProps<typeof ComboboxChipsInput>, 'placeholder'> &
  Omit<ComponentProps<typeof ComboboxChips>, 'value' | 'onValueChange' | 'children' | 'showClear'> &
  QueryProps &
  CreatableProps<T> & {
    /** The selected items. */
    value?: T[];
    /** Fired with the full new selection (an empty array when cleared). */
    onValueChange?: (value: T[]) => void;
    /** Whether a page is currently in-flight. Suppresses repeat `onLoadMore` fires while true. */
    isLoading?: boolean;
    /** Whether more pages remain. When false, the tail probe is not rendered. */
    hasMore?: boolean;
    /** Fired when the user scrolls near the bottom of the loaded set. Detected via an IntersectionObserver on a 1px probe element after the rows. */
    onLoadMore?: () => void;
    /** Extra content rendered in the popup below the list, outside the filtered collection (e.g. `ComboboxActions`). */
    children?: ReactNode;
    /** Blocks opening, typing, clearing and chip removal, without the disabled styling. */
    readOnly?: boolean;
    /**
     * Renders a clear affordance: in the control at the default size, on the collapsed summary chip at `size="sm"`,
     * where the control's one button slot has to stay with the chevron. Needs `collapseMultiple` to show at `sm`;
     * without it, individual chips carry their own remove buttons instead.
     */
    showClear?: boolean;
  };

type InFlightState<T> = { status: 'idle' } | { status: 'pending' | 'sticky'; value: T[] };

const IDLE: InFlightState<never> = { status: 'idle' };

export const MultiCombobox = <T,>({
  items,
  value,
  onValueChange,
  getKey,
  getLabel,
  isItemEqualToValue,
  itemToStringLabel,
  placeholder,
  emptyMessage,
  disabled,
  isLoading = false,
  hasMore = false,
  onLoadMore,
  query,
  onQueryChange,
  onCreate,
  renderCreateOption,
  id,
  collapseMultiple = false,
  children,
  readOnly,
  size = 'default',
  showClear = false,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired,
  'aria-describedby': ariaDescribedBy,
  ...chipsProps
}: MultiComboboxProps<T>) => {
  const anchor = useComboboxAnchor();

  const clearOnChip = showClear && size === 'sm';

  const isServerFilter = query !== undefined && onQueryChange !== undefined;
  const { typedQuery, handleInputValueChange, clearQuery } = useComboboxInputValue(
    isServerFilter ? onQueryChange : undefined
  );
  const [inFlight, setInFlight] = useState<InFlightState<T>>(IDLE);

  const { itemsForView, localFilter, stableItemToStringLabel } = useCreatableHelpers(
    items,
    getLabel,
    itemToStringLabel,
    onCreate,
    typedQuery ?? ''
  );

  const defaultIsItemEqualToValue = useCreatableItemEquality(getKey);
  const effectiveIsItemEqualToValue = isItemEqualToValue ?? defaultIsItemEqualToValue;

  const handleValueChange = useCallback<NonNullable<ComboboxRootProps<T, true>['onValueChange']>>(
    async (next) => {
      const hasCreatable = next.some(isCreatable);

      if (hasCreatable && inFlight.status === 'sticky') {
        setInFlight({ status: 'sticky', value: next });
        onValueChange?.(next.filter((item) => !isCreatable(item)) as T[]);
        return;
      }

      if (hasCreatable && onCreate) {
        const creatable = next.find(isCreatable) as unknown as CreatableMarker;
        setInFlight({ status: 'pending', value: next });

        try {
          const created = await onCreate(creatable.__create);
          const cleaned = next.filter((item) => !isCreatable(item)) as T[];
          onValueChange?.([...cleaned, created]);
          setInFlight(IDLE);
          clearQuery();
        } catch {
          setInFlight({ status: 'sticky', value: next });
        }

        return;
      }

      setInFlight(IDLE);
      onValueChange?.(next);
    },
    [onCreate, onValueChange, clearQuery, inFlight.status]
  );

  const valueForRoot = inFlight.status === 'idle' ? (value ?? []) : inFlight.value;

  const showLoadingCopy = isLoading && !items?.length;

  return (
    <ComboboxRoot
      multiple
      items={itemsForView}
      value={valueForRoot}
      onValueChange={handleValueChange}
      disabled={disabled}
      readOnly={readOnly}
      loopFocus={!hasMore}
      onOpenChange={(open, details) => {
        // Base UI closes after a pick while the input holds a query, so picking several matches means retyping it.
        if (!open && details.reason === 'item-press') {
          details.cancel();
        }
      }}
      itemToStringLabel={stableItemToStringLabel}
      isItemEqualToValue={effectiveIsItemEqualToValue}
      inputValue={typedQuery ?? ''}
      onInputValueChange={(next, details) => {
        if (inFlight.status !== 'idle') {
          setInFlight(IDLE);
        }
        handleInputValueChange(next, details.reason);
      }}
      filter={isServerFilter ? null : localFilter}
    >
      <ComboboxChips
        ref={anchor}
        collapseMultiple={collapseMultiple}
        disabled={disabled}
        readOnly={readOnly}
        size={size}
        showClear={showClear && !clearOnChip}
        {...chipsProps}
      >
        <ComboboxValue>
          {collapseMultiple && valueForRoot.length > 1 ? (
            <ComboboxMultipleLabel
              selectedItems={valueForRoot.filter((item) => !isCreatable(item))}
              itemToString={getLabel}
              showClear={clearOnChip}
            />
          ) : (
            valueForRoot.map((item) => {
              if (isCreatable(item)) {
                return <ComboboxChip key={`__create__${item.__create}`}>{item.__create}</ComboboxChip>;
              }
              return <ComboboxChip key={getKey(item)}>{getLabel(item)}</ComboboxChip>;
            })
          )}
        </ComboboxValue>
        <ComboboxChipsInput
          id={id}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={ariaInvalid}
          aria-required={ariaRequired}
          aria-describedby={ariaDescribedBy}
        />
      </ComboboxChips>
      <ComboboxPresetContent
        anchor={anchor}
        items={itemsForView}
        getKey={getKey}
        getLabel={getLabel}
        isLoading={isLoading}
        hasMore={hasMore}
        onLoadMore={onLoadMore}
        renderCreateOption={renderCreateOption}
        isServerFilter={isServerFilter}
        emptyMessage={emptyMessage}
        showLoadingCopy={showLoadingCopy}
      >
        {children}
      </ComboboxPresetContent>
    </ComboboxRoot>
  );
};
